import React, { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';

import { withTooltip } from '@crossnokaye/ux-common';
import {
  ActionBarActions,
  ActionBarCancelButton,
  ActionBarRoot,
  ActionBarSaveButton,
  ActionBarStatus,
} from '@crossnokaye/ux-common/ckds';

import { SettingsSaveDialog } from '@/components/settings/settings-save-dialog';
import { DetailsFormData } from '@/features/equipment-browser/equipment-details/details-form/equipment-details-form.types';
import { PRIVILEGES } from '@/hooks/access/access-table';
import { useActionPermission } from '@/hooks/use-action-permission';

import { useDetailsSettingsSave } from '../hooks/use-details-settings-save';

const SaveBarSaveButtonWithTooltip = withTooltip(ActionBarSaveButton);

type EquipmentDetailsFormSaveBarProps = {
  onDiscard: () => Promise<void>;
};

/**
 * EquipmentDetailsFormSaveBar
 *
 * Sticky save bar that appears when the form has unsaved changes.
 * Uses the shared SettingsSaveDialog for all settings — scalar settings show
 * field-level before/after diffs, while sequencer config and defrost schedule
 * show descriptive messages.
 */
export const EquipmentDetailsFormSaveBar: React.FC<EquipmentDetailsFormSaveBarProps> = ({
  onDiscard,
}) => {
  const { formState } = useFormContext<DetailsFormData>();
  const { isDisabled: disabledByPermissions, tooltipText } = useActionPermission({
    privilege: PRIVILEGES.writeCommands,
  });

  const {
    settingsSave,
    groups,
    changeCount,
    handleDiscard,
    openDialog: openSaveDialog,
  } = useDetailsSettingsSave();

  const { isDirty, isSubmitting, isValid } = formState;
  const disableSave = !isDirty || !isValid || isSubmitting || disabledByPermissions;

  const openDialog = useCallback(() => {
    if (!disableSave) {
      openSaveDialog();
    }
  }, [disableSave, openSaveDialog]);

  const handleClose = useCallback(() => {
    settingsSave.close();
  }, [settingsSave]);

  const isSaving = isSubmitting || settingsSave.hasPendingSave;

  return (
    <>
      <ActionBarRoot open={isDirty} className="relative">
        <ActionBarStatus
          statusText={
            isSaving
              ? 'Saving equipment updates\u2026'
              : `${changeCount} setting${changeCount === 1 ? '' : 's'} changed`
          }
        />
        <ActionBarActions>
          <ActionBarCancelButton onCancel={() => onDiscard()} isSubmitting={isSaving}>
            Cancel
          </ActionBarCancelButton>
          <SaveBarSaveButtonWithTooltip
            type="button"
            onClick={openDialog}
            disabled={disableSave}
            isSubmitting={isSaving}
            tooltipContent={tooltipText}
            delayDuration={0}
          >
            {`Review & Save ${changeCount} Change${changeCount === 1 ? '' : 's'}`}
          </SaveBarSaveButtonWithTooltip>
        </ActionBarActions>
      </ActionBarRoot>

      <SettingsSaveDialog
        open={settingsSave.isOpen}
        onOpenChange={(open) => {
          if (!open) handleClose();
        }}
        groups={groups}
        saveProgressByChangeId={settingsSave.saveProgressByChangeId}
        hasPendingSave={settingsSave.hasPendingSave}
        saveComplete={settingsSave.saveComplete}
        progressStats={settingsSave.progressStats}
        onDiscard={handleDiscard}
        onConfirm={() => settingsSave.confirmSave()}
        onCancelPending={settingsSave.cancelPending}
        onClose={handleClose}
      />
    </>
  );
};
